import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Button,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  CreditCard as BillingIcon,
  CheckCircle as CheckIcon,
  Cancel as CrossIcon,
  Refresh as RefreshIcon,
  Receipt as InvoiceIcon,
  DataUsage as UsageIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { usePermission } from '../hooks/usePermission';
import { useAuth } from '../contexts/AuthContext';

const serverMessage = (err, fallback) => err?.response?.data?.message || err?.response?.data?.errors?.[0]?.msg || err?.message || fallback;

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '-');
const formatMoney = (value) => (typeof value === 'number' ? `$${value.toLocaleString()}` : '-');
const formatNumber = (n) => (typeof n === 'number' ? n.toLocaleString() : '0');
const formatLimit = (limit) => (limit === -1 ? 'Unlimited' : formatNumber(limit));

const STATUS_COLORS = { active: 'success', trialing: 'info', past_due: 'warning', cancelled: 'error', canceled: 'error', inactive: 'default' };

/** Usage bar; a limit of -1 means unlimited. */
const UsageBar = ({ label, used = 0, limit, unit = '' }) => {
  const unlimited = limit === -1 || limit === undefined || limit === null;
  const percent = unlimited || !limit ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const color = percent >= 90 ? 'error' : percent >= 75 ? 'warning' : 'primary';
  return (
    <Box sx={{ mb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body2">{label}</Typography>
        <Typography variant="caption" color="text.secondary">
          {formatNumber(used)}{unit} of {unlimited ? 'unlimited' : `${formatNumber(limit)}${unit}`}
          {!unlimited && ` (${percent}%)`}
        </Typography>
      </Box>
      <LinearProgress variant="determinate" value={unlimited ? 0 : percent} color={color} sx={{ height: 8, borderRadius: 4, mt: 0.5 }} />
    </Box>
  );
};

const Billing = () => {
  const { canManageBilling, isCompanyAdmin } = usePermission();
  const { refreshUser } = useAuth();
  const canAct = canManageBilling || isCompanyAdmin;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [current, setCurrent] = useState(null); // { subscription, limits, usage }
  const [plans, setPlans] = useState([]);
  const [usage, setUsage] = useState(null);
  const [usageError, setUsageError] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoicesError, setInvoicesError] = useState(null);

  const [billingCycle, setBillingCycle] = useState('monthly');
  const [confirmPlan, setConfirmPlan] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [currentRes, plansRes, usageRes, invoicesRes] = await Promise.allSettled([
      apiService.billing.getCurrentPlan(),
      apiService.billing.getPlans(),
      canAct ? apiService.billing.getUsage() : Promise.reject(new Error('skip')),
      canAct ? apiService.billing.getInvoices() : Promise.reject(new Error('skip')),
    ]);

    const problems = [];
    if (currentRes.status === 'fulfilled') {
      setCurrent(currentRes.value.data);
      if (currentRes.value.data?.subscription?.billingCycle) setBillingCycle(currentRes.value.data.subscription.billingCycle);
    } else {
      problems.push(serverMessage(currentRes.reason, 'Failed to load the current plan'));
    }
    if (plansRes.status === 'fulfilled') {
      setPlans((plansRes.value.data?.plans || []).filter(p => p.isActive !== false).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)));
    } else {
      problems.push(serverMessage(plansRes.reason, 'Failed to load subscription plans'));
    }
    if (usageRes.status === 'fulfilled') {
      setUsage(usageRes.value.data?.usage || null);
      setUsageError(null);
    } else if (canAct) {
      setUsageError(serverMessage(usageRes.reason, 'Failed to load usage'));
    }
    if (invoicesRes.status === 'fulfilled') {
      setInvoices(invoicesRes.value.data?.invoices || []);
      setInvoicesError(null);
    } else if (canAct) {
      // 404 = no subscription record yet; treat as "no invoices"
      const status = invoicesRes.reason?.response?.status;
      setInvoices([]);
      setInvoicesError(status === 404 ? null : serverMessage(invoicesRes.reason, 'Failed to load invoices'));
    }
    if (problems.length) setError(problems.join(' '));
    setLoading(false);
  }, [canAct]);

  useEffect(() => { load(); }, [load]);

  const subscription = current?.subscription || {};
  const currentPlanName = subscription.plan || usage?.subscription?.plan || 'trial';
  const currentPlan = useMemo(() => plans.find(p => p.name === currentPlanName), [plans, currentPlanName]);

  // Usage numbers: admins get the billing usage endpoint; everyone else falls
  // back to the limits/usage included with the current plan.
  const usageCurrent = usage?.current || current?.usage || {};
  const usageLimits = usage?.limits || current?.limits || {};

  const handleChangePlan = async () => {
    if (!confirmPlan) return;
    try {
      setBusy(true);
      setError(null);
      const { data } = await apiService.billing.updatePlan({ plan: confirmPlan.name, billingCycle });
      setSuccess(data.message || `Plan changed to ${confirmPlan.displayName}`);
      setConfirmPlan(null);
      await refreshUser().catch(() => {});
      await load();
    } catch (err) {
      setError(serverMessage(err, 'Failed to change plan'));
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    try {
      setBusy(true);
      setError(null);
      const { data } = await apiService.billing.cancel();
      setSuccess(data.message || 'Subscription will be cancelled at the end of the current billing period');
      setConfirmCancel(false);
      await refreshUser().catch(() => {});
      await load();
    } catch (err) {
      setError(serverMessage(err, 'Failed to cancel subscription'));
    } finally {
      setBusy(false);
    }
  };

  const planPrice = (plan) => (billingCycle === 'yearly' ? plan.price?.yearly : plan.price?.monthly);

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>Billing</Typography>
          <Typography variant="body1" color="text.secondary">
            Your subscription plan, usage against plan limits, and invoices.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={busy}>Refresh</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mt: 3 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mt: 3 }} onClose={() => setSuccess(null)}>{success}</Alert>}
      {!canAct && (
        <Alert severity="info" sx={{ mt: 3 }}>
          You can view the current plan, but changing or cancelling the subscription requires the billing
          management permission (company administrators).
        </Alert>
      )}
      <Alert severity="warning" sx={{ mt: 3 }}>
        Online payment is not yet connected. Changing plans updates your limits immediately; invoicing and
        payment are handled manually by the Excubia team.
      </Alert>

      <Grid container spacing={3} sx={{ mt: 0 }}>
        {/* Current plan */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              avatar={<BillingIcon />}
              title="Current Plan"
              action={
                subscription.status && (
                  <Chip label={subscription.status} color={STATUS_COLORS[subscription.status] || 'default'} size="small" sx={{ mt: 1, mr: 1 }} />
                )
              }
            />
            <CardContent>
              <Typography variant="h5" gutterBottom>
                {currentPlan?.displayName || currentPlanName}
              </Typography>
              {currentPlan?.description && (
                <Typography variant="body2" color="text.secondary" gutterBottom>{currentPlan.description}</Typography>
              )}
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Billing cycle</Typography>
                  <Typography variant="body2">{subscription.billingCycle || 'monthly'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Amount</Typography>
                  <Typography variant="body2">
                    {formatMoney(subscription.amount)}{typeof subscription.amount === 'number' ? ` / ${subscription.billingCycle === 'yearly' ? 'year' : 'month'}` : ''}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Current period ends</Typography>
                  <Typography variant="body2">{formatDate(subscription.currentPeriodEnd)}</Typography>
                </Grid>
                {subscription.trialEndsAt && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Trial ends</Typography>
                    <Typography variant="body2">{formatDate(subscription.trialEndsAt)}</Typography>
                  </Grid>
                )}
              </Grid>
              {subscription.cancelAtPeriodEnd && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  This subscription is set to cancel at the end of the current period ({formatDate(subscription.currentPeriodEnd)}).
                  Choosing a plan below will reactivate it.
                </Alert>
              )}
            </CardContent>
            {canAct && !subscription.cancelAtPeriodEnd && currentPlanName !== 'trial' && (
              <CardActions sx={{ px: 2, pb: 2 }}>
                <Button color="error" variant="outlined" onClick={() => setConfirmCancel(true)} disabled={busy}>
                  Cancel subscription
                </Button>
              </CardActions>
            )}
          </Card>
        </Grid>

        {/* Usage */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader avatar={<UsageIcon />} title="Usage" subheader="Current usage against your plan limits" />
            <CardContent>
              {usageError && <Alert severity="info" sx={{ mb: 2 }}>{usageError}</Alert>}
              <UsageBar label="Users" used={usageCurrent.currentUsers} limit={usageLimits.maxUsers} />
              <UsageBar label="Detections" used={usageCurrent.currentDetections} limit={usageLimits.maxDetections} />
              <UsageBar label="Cases" used={usageCurrent.currentCases} limit={usageLimits.maxCases} />
              <UsageBar
                label="Storage"
                used={usageCurrent.storageUsedMB}
                limit={usageLimits.storageGB === -1 ? -1 : (usageLimits.storageGB || 0) * 1024}
                unit=" MB"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Plans */}
        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
            <Typography variant="h5">Plans</Typography>
            <ToggleButtonGroup
              value={billingCycle}
              exclusive
              size="small"
              onChange={(e, value) => value && setBillingCycle(value)}
            >
              <ToggleButton value="monthly">Monthly</ToggleButton>
              <ToggleButton value="yearly">Yearly</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Grid container spacing={3}>
            {plans.map(plan => {
              const isCurrent = plan.name === currentPlanName;
              const selectable = plan.name !== 'trial';
              return (
                <Grid item xs={12} sm={6} lg={3} key={plan.name}>
                  <Card
                    variant={isCurrent ? 'elevation' : 'outlined'}
                    sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderColor: isCurrent ? 'primary.main' : undefined, borderWidth: isCurrent ? 2 : 1, borderStyle: 'solid' }}
                  >
                    <CardHeader
                      title={plan.displayName}
                      subheader={plan.description}
                      action={isCurrent ? <Chip icon={<StarIcon />} label="Current" color="primary" size="small" sx={{ mt: 1, mr: 1 }} /> : null}
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h4" component="div">
                        {formatMoney(planPrice(plan))}
                        <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 0.5 }}>
                          / {billingCycle === 'yearly' ? 'year' : 'month'}
                        </Typography>
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {formatLimit(plan.limits?.maxUsers)} users, {formatLimit(plan.limits?.maxDetections)} detections,
                        {' '}{formatLimit(plan.limits?.maxCases)} cases, {formatLimit(plan.limits?.storageGB)} GB storage
                      </Typography>
                      <List dense disablePadding>
                        {(plan.features || []).map(feature => (
                          <ListItem key={feature.name} disableGutters sx={{ py: 0.25 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              {feature.included ? <CheckIcon color="success" fontSize="small" /> : <CrossIcon color="disabled" fontSize="small" />}
                            </ListItemIcon>
                            <ListItemText
                              primary={feature.name}
                              secondary={feature.included && feature.limit > 0 ? `up to ${formatNumber(feature.limit)}` : null}
                              primaryTypographyProps={{ variant: 'body2', color: feature.included ? 'text.primary' : 'text.disabled' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                    <CardActions sx={{ px: 2, pb: 2 }}>
                      {isCurrent && !subscription.cancelAtPeriodEnd ? (
                        <Button fullWidth disabled variant="outlined">Current plan</Button>
                      ) : selectable ? (
                        <Tooltip title={canAct ? '' : 'Requires billing management permission'}>
                          <span style={{ width: '100%' }}>
                            <Button
                              fullWidth
                              variant={isCurrent ? 'outlined' : 'contained'}
                              disabled={!canAct || busy}
                              onClick={() => setConfirmPlan(plan)}
                            >
                              {isCurrent ? 'Reactivate' : (currentPlan && (plan.sortOrder || 0) < (currentPlan.sortOrder || 0)) ? 'Downgrade' : 'Upgrade'}
                            </Button>
                          </span>
                        </Tooltip>
                      ) : (
                        <Button fullWidth disabled variant="outlined">Trial only</Button>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Grid>

        {/* Invoices */}
        {canAct && (
          <Grid item xs={12}>
            <Card>
              <CardHeader avatar={<InvoiceIcon />} title="Invoices" subheader="Invoices are issued manually until online payment is connected" />
              <CardContent>
                {invoicesError && <Alert severity="info" sx={{ mb: 2 }}>{invoicesError}</Alert>}
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Period</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoices.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography color="text.secondary">No invoices yet</Typography>
                          </TableCell>
                        </TableRow>
                      ) : invoices.map((inv, index) => (
                        <TableRow key={inv.id || inv._id || inv.invoiceNumber || index}>
                          <TableCell>{inv.invoiceNumber || inv.number || inv.id || inv._id || `#${index + 1}`}</TableCell>
                          <TableCell>{formatDate(inv.date || inv.createdAt || inv.issuedAt)}</TableCell>
                          <TableCell>
                            {inv.periodStart || inv.periodEnd ? `${formatDate(inv.periodStart)} - ${formatDate(inv.periodEnd)}` : '-'}
                          </TableCell>
                          <TableCell align="right">{formatMoney(inv.amount)}</TableCell>
                          <TableCell>
                            {inv.status ? <Chip size="small" label={inv.status} color={inv.status === 'paid' ? 'success' : inv.status === 'open' ? 'warning' : 'default'} /> : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Plan change confirmation */}
      <Dialog open={Boolean(confirmPlan)} onClose={() => !busy && setConfirmPlan(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Change plan to {confirmPlan?.displayName}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            You are switching to the <strong>{confirmPlan?.displayName}</strong> plan billed{' '}
            <strong>{billingCycle}</strong> at {confirmPlan ? formatMoney(planPrice(confirmPlan)) : ''} per{' '}
            {billingCycle === 'yearly' ? 'year' : 'month'}.
          </DialogContentText>
          <Alert severity="info">
            Plan limits (users, detections, cases, storage) change immediately. Online payment is not connected
            yet, so no card will be charged: the Excubia team will invoice you manually for this plan.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPlan(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={handleChangePlan} disabled={busy}>
            {busy ? <CircularProgress size={20} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel confirmation */}
      <Dialog open={confirmCancel} onClose={() => !busy && setConfirmCancel(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel subscription</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your subscription will remain active until the end of the current billing period
            ({formatDate(subscription.currentPeriodEnd)}) and will not renew. You can reactivate it at any time
            by choosing a plan.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCancel(false)} disabled={busy}>Keep subscription</Button>
          <Button color="error" variant="contained" onClick={handleCancel} disabled={busy}>
            {busy ? <CircularProgress size={20} /> : 'Cancel at period end'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Billing;
